/**
 * Alarm / Timer sound system using Web Audio API.
 * All sounds are generated synthetically — no external files required.
 */

import type { Bilingual } from '../config/bilingual';

export interface SoundPreset {
  id: string;
  /** Bilingual: a data table cannot know the reader's language — the picker picks. */
  label: Bilingual;
  description: Bilingual;
}

export const SOUND_PRESETS: SoundPreset[] = [
  {
    id: 'classic-alarm',
    label: { en: 'Classic Alarm', sl: 'Klasičen alarm' },
    description: { en: 'Traditional alarm beeping', sl: 'Tradicionalno piskanje alarma' },
  },
  {
    id: 'gentle-chime',
    label: { en: 'Gentle Chime', sl: 'Nežno zvonjenje' },
    description: { en: 'Soft bell chimes', sl: 'Mehki zvončki' },
  },
  {
    id: 'digital-beep',
    label: { en: 'Digital Beep', sl: 'Digitalni pisk' },
    description: { en: 'Modern digital tone', sl: 'Sodobnejši digitalni ton' },
  },
  {
    id: 'bell',
    label: { en: 'Bell', sl: 'Zvonec' },
    description: { en: 'Single resonant bell', sl: 'Enkraten zveneč zvonec' },
  },
  {
    id: 'marimba',
    label: { en: 'Marimba', sl: 'Marimba' },
    description: { en: 'Xylophone-style tones', sl: 'Ksilofonski toni' },
  },
  {
    id: 'pulse',
    label: { en: 'Pulse', sl: 'Pulz' },
    description: { en: 'Electronic pulse beat', sl: 'Elektronski pulzni takt' },
  },
];

let audioCtx: AudioContext | null = null;
let loopTimeout: ReturnType<typeof setTimeout> | null = null;
/**
 * Every node a preset has scheduled, so `stopSound()` can silence them. An
 * oscillator carries `stop()` and a gain node does not — the stop call below is
 * optional because of that, read structurally here instead of casting each node.
 */
type ScheduledAudioNode = AudioNode & { stop?: () => void };
let activeNodes: ScheduledAudioNode[] = [];
let isLooping = false;

function getCtx(): AudioContext {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new AudioContext();
  }
  return audioCtx;
}

function playTone(
  ctx: AudioContext,
  freq: number,
  type: OscillatorType,
  startTime: number,
  duration: number,
  gain = 0.6,
): void {
  const osc = ctx.createOscillator();
  const gainNode = ctx.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, startTime);

  gainNode.gain.setValueAtTime(0, startTime);
  gainNode.gain.linearRampToValueAtTime(gain, startTime + 0.01);
  gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

  osc.connect(gainNode);
  gainNode.connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + duration);

  activeNodes.push(osc, gainNode);
}

function scheduleSound(id: string, ctx: AudioContext, offset: number): number {
  const t = ctx.currentTime + offset;
  switch (id) {
    case 'classic-alarm': {
      // Three quick beeps
      for (let i = 0; i < 3; i++) {
        playTone(ctx, 880, 'square', t + i * 0.3, 0.2, 0.4);
      }
      return 1.5;
    }
    case 'gentle-chime': {
      const notes = [523, 659, 784, 1047];
      notes.forEach((f, i) => playTone(ctx, f, 'sine', t + i * 0.3, 0.5, 0.35));
      return 2.2;
    }
    case 'digital-beep': {
      playTone(ctx, 1000, 'square', t, 0.15, 0.3);
      playTone(ctx, 1200, 'square', t + 0.2, 0.15, 0.3);
      return 1.0;
    }
    case 'bell': {
      // Layered sine waves for bell-like sound
      playTone(ctx, 880, 'sine', t, 1.5, 0.5);
      playTone(ctx, 1760, 'sine', t, 0.8, 0.2);
      playTone(ctx, 2640, 'sine', t, 0.4, 0.1);
      return 2.5;
    }
    case 'marimba': {
      const scale = [523, 659, 784, 523];
      scale.forEach((f, i) => playTone(ctx, f, 'sine', t + i * 0.25, 0.3, 0.5));
      return 1.8;
    }
    case 'pulse': {
      for (let i = 0; i < 4; i++) {
        playTone(ctx, 440, 'sawtooth', t + i * 0.25, 0.15, 0.3);
      }
      return 1.5;
    }
    default: {
      playTone(ctx, 880, 'square', t, 0.2, 0.4);
      return 1.0;
    }
  }
}

/** Play a sound once (e.g., for preview) */
export async function playSound(id: string): Promise<void> {
  stopSound();
  const ctx = getCtx();
  if (ctx.state === 'suspended') await ctx.resume();
  scheduleSound(id, ctx, 0);
}

/** Play a sound in a loop until stopSound() is called */
export async function playSoundLoop(id: string): Promise<void> {
  stopSound();
  isLooping = true;
  const ctx = getCtx();
  if (ctx.state === 'suspended') await ctx.resume();

  const loop = () => {
    if (!isLooping) return;
    const duration = scheduleSound(id, ctx, 0);
    loopTimeout = setTimeout(loop, duration * 1000);
  };
  loop();
}

/** Stop any playing sound */
export function stopSound(): void {
  isLooping = false;
  if (loopTimeout) {
    clearTimeout(loopTimeout);
    loopTimeout = null;
  }
  activeNodes.forEach((node) => {
    try {
      node.stop?.();
    } catch {
      /* already stopped */
    }
    try {
      node.disconnect();
    } catch {
      /* already disconnected */
    }
  });
  activeNodes = [];
}
